import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ON_DEMAND_PRODUCT } from 'constants/products.js';
import useOrgId from 'hooks/useOrgId.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useAlert } from 'contexts/AlertContext.jsx';
import useNotifications from 'hooks/useNotifications.js';
import { buildRoute, ORG_ON_DEMAND_VIDEOS } from 'constants/routes.js';
import useAddMediaAssetMutation from './useAddMediaAssetMutation.js';
import usePatchMediaAssetMutation from './usePatchMediaAssetMutation.js';
import useUploadStatusQuery, { UPLOAD_STATUS } from './useUploadStatusQuery.js';
import useVideoQuery from './useVideoQuery.js';

let fileId = 0;
const nextFileId = () => {
  fileId += 1;
  return `file_${fileId}`;
};

const GenerativeJobPoller = ({ videoId, onComplete, onError }) => {
  const doneRef = useRef(false);
  const [enabled, setEnabled] = useState(true);
  useVideoQuery({ videoId }, {
    enabled,
    refetchInterval: 15000,
    onSuccess: (result) => {
      if (doneRef.current) return;
      const video = result?.data;
      if (!video || video.deletedAt) {
        doneRef.current = true;
        setEnabled(false);
        onError(videoId);
      } else if (video.mediaAssets?.length > 0) {
        doneRef.current = true;
        setEnabled(false);
        onComplete(videoId);
      }
    },
  });
  return null;
};

const ProcessingVideoPoller = ({ videoId, vendorUploadId, mediaAssetId, progress, onProgress, onComplete }) => {
  useUploadStatusQuery({ mediaAssetId, vendorUploadId, progress, state: UPLOAD_STATUS.PROCESSING }, {
    enabled: !!mediaAssetId,
    onSuccess: ({ state, progress }) => {
      if (state === UPLOAD_STATUS.COMPLETED) {
        onComplete(videoId);
      } else if (state !== UPLOAD_STATUS.UPLOADING) {
        onProgress({ videoId, vendorUploadId, progress });
      }
    },
  });
  return null;
};

// Create the context
export const VideoUploadContext = createContext({
  uploadingVideos: null,
  isUploadingVideo: false,
  uploadVideo: () => {},
  updateProcessingProgress: () => {},
  handleProcessingMediaAsset: () => {},
  trackGenerativeJob: () => {},
});

// Create the provider
export const VideoUploadContextProvider = ({ children }) => {
  const orgId = useOrgId();
  const { setAlert } = useAlert();
  const history = useHistory();
  const { handleFooterNotification } = useNotifications();
  const { t } = useLocalization();
  const [uploadingVideos, setUploadingVideos] = useState({});

  const hasUploadingVideos = Object.values(uploadingVideos).some((video) => {
    return Object.values(video).some(({ state }) => state === UPLOAD_STATUS.UPLOADING);
  });

  const isUploadingVideo = hasUploadingVideos || Object.values(uploadingVideos).some((video) => {
    return Object.values(video).some(({ state }) => state === UPLOAD_STATUS.PROCESSING);
  });

  const [pendingGenerativeJobs, setPendingGenerativeJobs] = useState({});

  const trackGenerativeJob = useCallback(({ videoId }) => {
    if (!videoId) return;
    setPendingGenerativeJobs((prev) => ({ ...prev, [videoId]: true }));
  }, []);

  const handleGenerativeJobComplete = useCallback((videoId) => {
    setPendingGenerativeJobs((prev) => {
      const updated = { ...prev };
      delete updated[videoId];
      return updated;
    });
    if (history.location.pathname.includes(`/${ON_DEMAND_PRODUCT}/videos`)) return;
    handleFooterNotification({
      title: t('generateVideoDrawer.videoGeneratedTitle'),
      message: t('videoSource.videoUploadedDescription'),
      actionText: t('getStartedVodPage.goToVideosButton'),
      action: () => history.push(buildRoute(ORG_ON_DEMAND_VIDEOS, { orgId })),
    });
  }, [handleFooterNotification, history, orgId, t]);

  const handleGenerativeJobError = useCallback((videoId) => {
    setPendingGenerativeJobs((prev) => {
      const updated = { ...prev };
      delete updated[videoId];
      return updated;
    });
    setAlert({ title: t('generateVideoDrawer.errorTitle'), variant: 'warning' });
  }, [setAlert, t]);

  const fileObjectToFileIdMap = useRef(new Map());
  const uploadIdToFileIdMap = useRef(new Map());
  const fileIdToVideoIdMap = useRef(new Map());
  const activeUploadCountRef = useRef(0);

  const idForFile = (file) => {
    if (!fileObjectToFileIdMap.current.get(file)) {
      fileObjectToFileIdMap.current.set(file, nextFileId());
    }
    return fileObjectToFileIdMap.current.get(file);
  };

  const cleanupFile = (file) => {
    fileObjectToFileIdMap.current.delete(file);
  };

  useEffect(() => {
    if (!orgId) return;
    const showVideosUploadingAlert = () => {
      setAlert({
        title: t('genericLabels.uploadInProgress'),
        variant: 'warning',
      });
    };

    const cleanupBlock = history.block((location) => {
      // it's always OK to navigate on pages belonging to the On demand product
      if (location.pathname.includes(`/${ON_DEMAND_PRODUCT}/`) || !hasUploadingVideos) {
        return true;
      }

      showVideosUploadingAlert();
      return false;
    });

    const beforeUnloadHandler = (e) => {
      if (hasUploadingVideos) {
        e.preventDefault();
        setAlert({
          title: t('genericLabels.uploadInProgress'),
          variant: 'warning',
        });
      }
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      cleanupBlock();
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [history, orgId, setAlert, hasUploadingVideos, t]);

  const setUploadingVideoState = (videoId, fileId, newState) => {
    setUploadingVideos((prevUploadingVideos) => {
      const videoInfo = { ...prevUploadingVideos[videoId] || {} };
      const prevFileInfo = videoInfo[fileId] || {};
      const { mediaAssetId: prevMediaAssetId, vendorUploadId: prevVendorUploadId } = prevFileInfo;
      const mediaAssetId = newState.mediaAssetId || prevMediaAssetId;
      const vendorUploadId = newState.vendorUploadId || prevVendorUploadId;
      const fileInfo = { ...newState, ...{ mediaAssetId, vendorUploadId } };
      videoInfo[fileId] = fileInfo;
      return {
        ...prevUploadingVideos,
        [videoId]: videoInfo,
      };
    });
  };

  const onUploadProgress = (file, didStart, uploadedSize, totalSize, mediaAssetId) => {
    const fileId = idForFile(file);
    const videoId = fileIdToVideoIdMap.current.get(fileId);
    const progress = (didStart && totalSize > 0) ? Math.floor(100 * (uploadedSize / totalSize / 2)) : 0;
    if (videoId) {
      setUploadingVideoState(videoId, fileId, { state: 'uploading', progress, mediaAssetId });
    }
  };

  const showCompletionNotification = useCallback(() => {
    if (history.location.pathname.includes(`/${ON_DEMAND_PRODUCT}/videos`)) return;
    handleFooterNotification({
      title: t('genericLabels.uploadSuccess'),
      message: t('videoSource.videoUploadedDescription'),
      type: 'default',
      actionText: t('getStartedVodPage.goToVideosButton'),
      action: () => history.push(buildRoute(ORG_ON_DEMAND_VIDEOS, { orgId })),
      autoHide: false,
    });
  }, [handleFooterNotification, history, orgId, t]);

  const handleFileError = (error, fileId) => {
    if (activeUploadCountRef.current > 0) activeUploadCountRef.current--;
    const videoId = fileIdToVideoIdMap.current.get(fileId);
    if (videoId) {
      setUploadingVideoState(videoId, fileId, { error });
      setAlert({
        title: t('uploadPreRecorded.failedToUploadFileError'),
        variant: 'warning',
      });
    }
  };

  const onUploadError = (error, file) => {
    const fileId = idForFile(file);
    cleanupFile(file);
    handleFileError(error, fileId);
  };

  const onSetUploadIdSuccess = ({ vendorUploadId }) => {
    const fileId = uploadIdToFileIdMap.current.get(vendorUploadId);
    if (!fileId) return;
    const videoId = fileIdToVideoIdMap.current.get(fileId);
    if (!videoId) return;
    setUploadingVideoState(videoId, fileId, { state: UPLOAD_STATUS.PROCESSING, progress: 50, vendorUploadId });
  };

  const onSetUploadIdError = (error, { data: { vendorUploadId } }) => {
    const fileId = uploadIdToFileIdMap.current.get(vendorUploadId);
    if (fileId) handleFileError(error, fileId);
  };

  const setMediaAssetUploadIdMutation = usePatchMediaAssetMutation({ onSuccess: onSetUploadIdSuccess, onError: onSetUploadIdError });
  const onUploadSuccess = ({ file, uploadId, mediaAssetId }) => {
    const fileId = idForFile(file);
    cleanupFile(file);

    const videoId = fileIdToVideoIdMap.current.get(fileId);
    if (videoId) {
      setUploadingVideoState(videoId, fileId, { state: 'uploading', progress: 50, vendorUploadId: uploadId, mediaAssetId });
      uploadIdToFileIdMap.current.set(uploadId, fileId);
      setMediaAssetUploadIdMutation.mutateAsync({ mediaAssetId, data: { vendorUploadId: uploadId } });
    }
  };

  const isAborted = () => false;
  const addMediaAssetMutation = useAddMediaAssetMutation({ onProgress: onUploadProgress, onError: onUploadError, onSuccess: onUploadSuccess, isAborted });
  const uploadVideo = ({ videoId, file, sourceUrl, customData, platform, importData }) => {
    activeUploadCountRef.current++;
    const fileId = idForFile(file);
    fileIdToVideoIdMap.current.set(fileId, videoId);
    setUploadingVideoState(videoId, fileId, { state: UPLOAD_STATUS.UPLOADING, progress: 0 });
    addMediaAssetMutation.mutateAsync({ videoId, file, orgId, sourceUrl, customData, platform, importData });
  };

  const updateProcessingProgress = ({ videoId, vendorUploadId, progress }) => {
    const fileId = uploadIdToFileIdMap.current.get(vendorUploadId);
    if (fileId && videoId) {
      setUploadingVideoState(videoId, fileId, { state: UPLOAD_STATUS.PROCESSING, vendorUploadId, progress });
    }
  };

  const handleProcessingMediaAsset = ({ videoId, mediaAssetId, vendorUploadId }) => {
    // only handle incoming processing video if we do not already have it
    if (!uploadIdToFileIdMap.current.has(vendorUploadId)) {
      activeUploadCountRef.current++;
      const fileId = nextFileId();
      fileIdToVideoIdMap.current.set(fileId, videoId);
      uploadIdToFileIdMap.current.set(vendorUploadId, fileId);
      setUploadingVideoState(videoId, fileId, { state: UPLOAD_STATUS.PROCESSING, progress: 50, vendorUploadId, mediaAssetId });
    }
  };

  const handleProcessingComplete = useCallback((videoId) => {
    if (activeUploadCountRef.current > 0) activeUploadCountRef.current--;
    if (activeUploadCountRef.current === 0) showCompletionNotification();
    setUploadingVideos((prev) => {
      if (!prev[videoId]) return prev;
      const updated = { ...prev };
      delete updated[videoId];
      return updated;
    });
  }, [showCompletionNotification]);

  const handleProcessingProgress = useCallback(({ videoId, vendorUploadId, progress }) => {
    const fileId = uploadIdToFileIdMap.current.get(vendorUploadId);
    if (fileId && videoId) {
      setUploadingVideoState(videoId, fileId, { state: UPLOAD_STATUS.PROCESSING, vendorUploadId, progress });
    }
  }, []);


  const processingPollers = Object.entries(uploadingVideos).reduce((videos, [videoId, files]) => {
    for (const { state, mediaAssetId, vendorUploadId, progress } of Object.values(files)) {
      if (state !== 'processing' || !mediaAssetId) continue;
      videos.push(
        <ProcessingVideoPoller
          key={vendorUploadId}
          videoId={videoId}
          mediaAssetId={mediaAssetId}
          vendorUploadId={vendorUploadId}
          progress={progress}
          onProgress={handleProcessingProgress}
          onComplete={handleProcessingComplete}
        />,
      );
    }
    return videos;
  }, []);

  const generativeJobPollers = Object.keys(pendingGenerativeJobs).map((videoId) => (
    <GenerativeJobPoller
      key={videoId}
      videoId={videoId}
      onComplete={handleGenerativeJobComplete}
      onError={handleGenerativeJobError}
    />
  ));

  return (
    <VideoUploadContext.Provider
      value={{
        uploadingVideos,
        isUploadingVideo,
        uploadVideo,
        updateProcessingProgress,
        handleProcessingMediaAsset,
        trackGenerativeJob,
      }}
    >
      {children}
      {processingPollers}
      {generativeJobPollers}
    </VideoUploadContext.Provider>
  );
};

export const useVideoUploads = () => useContext(VideoUploadContext);
